import React from 'react';
import { BookOpen, Check, Clock, Shield, Star, Users, Award, Zap, FileText, FlaskConical } from 'lucide-react';
import { MetaPixel } from './components/MetaPixel';
import { CTAButton } from './components/CTAButton';
import { PurchasePopup } from './components/PurchasePopup';
import { BonusCard } from './components/BonusCard';
import { TestimonialCard } from './components/TestimonialCard';
import { FAQItem } from './components/FAQItem';
export function App() {
  // Image URLs
  const mainImage = 'https://cdn.magicpatterns.com/uploads/pcvBDWh3uE4jcFRXXZBish/f09b870b-b94e-4eb9-a2c9-7c6fcb1184f8.png';
  const bonus1Image = 'https://cdn.magicpatterns.com/uploads/6FZppY7hxbrSZPGKbc4uWo/14.png';
  const bonus2Image = 'https://cdn.magicpatterns.com/uploads/rqx1MPTvbzYGSqGwQHthbP/15.png';
  const bonus3Image = 'https://cdn.magicpatterns.com/uploads/d7gvQZDspv94fEMwmGGG6y/16.png';
  const bonus4Image = 'https://cdn.magicpatterns.com/uploads/bVkeiqNrLYDQ8aw5kqStUy/17.png';
  // New images for bonuses 5 and 6
  const bonus5Image = 'https://cdn.magicpatterns.com/uploads/rwCqUkSXZh23aibfAjnFUc/18.png';
  const bonus6Image = 'https://cdn.magicpatterns.com/uploads/67U1L6SrMjmUjDCyamRJVh/19.png';
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <PurchasePopup data-id="element-2" />

      {/* Hero Section */}
      <header className="bg-gradient-to-b from-green-50 to-white pt-8 pb-12 md:pt-12 md:pb-16 px-4 overflow-hidden" data-id="element-3">
        <div className="max-w-6xl mx-auto" data-id="element-4">
          <div className="text-center max-w-4xl mx-auto mb-4" data-id="element-5">
            <div className="inline-block px-4 py-1.5 bg-green-100 text-green-800 font-bold rounded-full text-sm mb-3 uppercase tracking-wider" data-id="element-6">
              Para docentes de química
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight" data-id="element-7">
              ORGANIZÁ TODO TU AÑO DE{' '}
              <span className="text-green-600" data-id="element-8">QUÍMICA</span>
            </h1>
          </div>

          <div className="max-w-xs mx-auto mb-4" data-id="element-9">
            <img src={mainImage} alt="MegaPack Química Lista" className="w-full h-auto cursor-pointer" onClick={scrollToPurchase} data-id="element-10" />
          </div>

          <div className="text-center max-w-4xl mx-auto" data-id="element-11">
            <h2 className="text-lg md:text-xl font-medium text-gray-600 mb-3 max-w-2xl mx-auto" data-id="element-12">
              SIN ESTRÉS NI HORAS DE PLANIFICACIÓN
            </h2>
            <p className="text-base md:text-lg text-gray-600 mb-5 max-w-3xl mx-auto" data-id="element-13">
              Material listo para usar que te ahorra horas de preparación y te
              permite dar clases claras, prácticas y alineadas al programa
              escolar.
            </p>
            <div className="flex justify-center" data-id="element-14">
              <CTAButton size="xl" scrollToOffer={true} data-id="element-15">
                QUIERO ACCESO INMEDIATO
              </CTAButton>
            </div>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-white px-4" data-id="element-16">
        <div className="max-w-4xl mx-auto" data-id="element-17">
          <div className="bg-red-50 rounded-3xl p-8 md:p-12 border border-red-100 shadow-sm" data-id="element-18">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-19">
              Si sos docente de Química, esto te va a sonar muy familiar…
            </h3>

            <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-20">
              <p data-id="element-21">
                Llegás a tu casa cansado/a… Sabés que tenés que planificar…
                Abrís la compu… Y empezás otra vez de cero.
              </p>

              <p data-id="element-22">
                Buscando actividades. Buscando prácticas de laboratorio.
                Buscando evaluaciones. Buscando ejemplos que se entiendan.
                Buscando formas de explicar lo difícil sin complicar más.
              </p>

              <p className="font-semibold text-red-700" data-id="element-23">
                Y el tiempo vuela. Y el cansancio crece. Y tu cabeza no para.
              </p>

              <p data-id="element-24">
                No porque no sepas enseñar. No porque no seas buen/a docente.{' '}
                <span className="font-bold bg-red-100 px-1" data-id="element-25">
                  Sino porque estás intentando sostener todo el año sin un
                  sistema que te sostenga a vos.
                </span>
              </p>
            </div>
          </div>

          <div className="mt-16 text-center" data-id="element-26">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-27">
              El problema real: Hoy el sistema educativo te exige...
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-id="element-28">
              {['Planificar', 'Armar actividades', 'Evaluar', 'Nivelar grupos', 'Preparar proyectos', 'Cumplir programas', 'Motivar alumnos', 'Registrar avances'].map(item => <div key={item} className="bg-gray-50 p-4 rounded-lg font-medium text-gray-700 border border-gray-100" data-id="element-29">
                  {item}
                </div>)}
            </div>
            <p className="text-xl text-gray-600 italic mb-8" data-id="element-30">
              ...todo al mismo tiempo. Y nadie te dio un sistema que te ahorre
              ese trabajo.
            </p>

            <div className="bg-gray-900 text-white p-6 rounded-xl inline-block max-w-2xl mx-auto" data-id="element-31">
              <p className="font-medium" data-id="element-32">
                Vivís improvisando, llevando trabajo a casa, corriendo detrás
                del programa y sintiendo que nunca terminás.
              </p>
            </div>

            <div className="mt-10 flex justify-center" data-id="element-33">
              <CTAButton scrollToOffer={true} data-id="element-34">
                TERMINAR CON EL ESTRÉS AHORA
              </CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-green-50 px-4" data-id="element-35">
        <div className="max-w-5xl mx-auto" data-id="element-36">
          <div className="text-center mb-16" data-id="element-37">
            <span className="text-green-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-38">
              La Solución Definitiva
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-39">
              MEGAPACK QUÍMICA LISTA™
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-40">
              Un sistema completo de materiales de Química organizados por nivel
              para que nunca más tengas que empezar de cero.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-12" data-id="element-41">
            {[{
            icon: Check,
            text: 'Todo listo'
          }, {
            icon: FlaskConical,
            text: 'Todo probado'
          }, {
            icon: FileText,
            text: 'Todo ordenado'
          }, {
            icon: Zap,
            text: 'Todo usable'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-md border border-green-100" data-id="element-42">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600" data-id="element-43">
                  <item.icon className="h-6 w-6" data-id="element-44" />
                </div>
                <h3 className="font-bold text-gray-900" data-id="element-45">{item.text}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white px-4" data-id="element-46">
        <div className="max-w-6xl mx-auto" data-id="element-47">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-48">
            <div className="w-full md:w-1/2" data-id="element-49">
              <img src={mainImage} alt="Contenido del pack" className="w-full rounded-xl shadow-2xl transform hover:scale-[1.02] transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-50" />
            </div>
            <div className="w-full md:w-1/2" data-id="element-51">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-52">
                ¿Qué incluye?
              </h2>
              <div className="flex items-center mb-6" data-id="element-53">
                <BookOpen className="h-6 w-6 text-green-600 mr-3" data-id="element-54" />
                <span className="text-xl font-bold text-gray-800" data-id="element-55">
                  +600 páginas de material digital
                </span>
              </div>
              <p className="text-gray-600 mb-8" data-id="element-56">
                Una biblioteca completa para todo tu año de Química.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-57">
                {['Guías de laboratorio listas', 'Teoría clara y secuenciada', 'Actividades prácticas', 'Evaluaciones y diagnósticos', 'Planillas docentes', 'Carpetas de campo', 'Proyectos anuales', 'Secuencias y planificaciones'].map((feature, i) => <div key={i} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-58">
                    <div className="h-2 w-2 rounded-full bg-green-500 mr-3" data-id="element-59"></div>
                    <span className="text-gray-700 font-medium" data-id="element-60">{feature}</span>
                  </div>)}
              </div>

              <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-lg text-blue-800 font-medium text-center" data-id="element-61">
                Para todo el sistema educativo, TODOS LOS NIVELES
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-62">
        <div className="max-w-6xl mx-auto" data-id="element-63">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-64">
            {/* Student Benefits */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-green-500" data-id="element-65">
              <div className="flex items-center mb-6" data-id="element-66">
                <div className="bg-green-100 p-3 rounded-full mr-4" data-id="element-67">
                  <Users className="h-8 w-8 text-green-600" data-id="element-68" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-69">
                  Qué van a lograr tus alumnos
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-70">
                {['Entender la Química paso a paso', 'Participar más en clase', 'Aprender haciendo', 'Comprender conceptos difíciles', 'Relacionar la Química con la vida real', 'Sentirse más seguros'].map((item, i) => <li key={i} className="flex items-start" data-id="element-71">
                    <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-72" />
                    <span className="text-gray-700" data-id="element-73">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Teacher Benefits */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-blue-500" data-id="element-74">
              <div className="flex items-center mb-6" data-id="element-75">
                <div className="bg-blue-100 p-3 rounded-full mr-4" data-id="element-76">
                  <Award className="h-8 w-8 text-blue-600" data-id="element-77" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900" data-id="element-78">
                  Qué vas a lograr vos
                </h3>
              </div>
              <ul className="space-y-4" data-id="element-79">
                {['Ahorrar horas todas las semanas', 'Dejar de improvisar', 'Tener respaldo profesional', 'Cubrir todo el programa', 'Dar clases más claras y dinámicas', 'Volver a disfrutar enseñar'].map((item, i) => <li key={i} className="flex items-start" data-id="element-80">
                    <Check className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-81" />
                    <span className="text-gray-700" data-id="element-82">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-83">
            <CTAButton scrollToOffer={true} data-id="element-84">QUIERO LOGRAR ESTO</CTAButton>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-green-50 px-4" data-id="element-85">
        <div className="max-w-6xl mx-auto" data-id="element-86">
          <div className="text-center mb-16" data-id="element-87">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-88">
              BONOS EXTRA INCLUIDOS
            </h2>
            <div className="inline-block px-6 py-2 bg-red-600 text-white font-bold rounded-full text-lg shadow-lg transform -rotate-2" data-id="element-89">
              GRATIS HOY CON TU COMPRA
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-90">
            <BonusCard bonusNumber={1} title="Mezclas, reacciones y energía" description="Ejercicios guiados para activar la práctica desde la semana 1." imageSrc={bonus1Image} data-id="element-91" />
            <BonusCard bonusNumber={2} title="Conceptos fundamentales" description="Base clara para nivelar grupos heterogéneos." imageSrc={bonus2Image} data-id="element-92" />
            <BonusCard bonusNumber={3} title="Química orgánica y vida cotidiana" description='Ejemplos reales para enganchar incluso a quienes "odian química".' imageSrc={bonus3Image} data-id="element-93" />
            <BonusCard bonusNumber={4} title="Cuaderno interactivo de actividades" description="Registros y experiencias listas para aplicar en clase." imageSrc={bonus4Image} data-id="element-94" />
            <BonusCard bonusNumber={5} title="Química general y orgánica" description="Puente completo para secuenciar contenidos durante todo el año." imageSrc={bonus5Image} data-id="element-95" />
            <BonusCard bonusNumber={6} title="Planilla de seguimiento" description="Rúbrica + criterios listos para evaluar sin improvisar." imageSrc={bonus6Image} data-id="element-96" />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="purchase" className="py-16 md:py-24 bg-gray-900 text-white px-4 relative overflow-hidden" data-id="element-97">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10" data-id="element-98">
          <div className="absolute top-10 left-10 w-64 h-64 bg-green-500 rounded-full blur-3xl" data-id="element-99"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-500 rounded-full blur-3xl" data-id="element-100"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-101">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-102">
            ACCESO COMPLETO DE POR VIDA
          </h2>
          <p className="text-xl text-gray-300 mb-12" data-id="element-103">
            Todo el material + Todos los bonos
          </p>

          <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl max-w-lg mx-auto border-4 border-green-500 transform hover:scale-[1.02] transition-transform duration-300" data-id="element-104">
            <div className="flex justify-center items-center mb-6 space-x-4" data-id="element-105">
              <span className="text-gray-400 line-through text-2xl font-medium" data-id="element-106">
                $49.99 USD
              </span>
              <span className="bg-red-100 text-red-600 px-3 py-1 rounded-lg font-bold text-sm" data-id="element-107">
                -75% OFF
              </span>
            </div>

            <div className="text-6xl md:text-7xl font-extrabold text-gray-900 mb-2 tracking-tight" data-id="element-108">
              $12.99{' '}
              <span className="text-2xl text-gray-500 font-normal" data-id="element-109">USD</span>
            </div>

            <p className="text-sm text-gray-500 mb-8" data-id="element-110">
              *Pagarás en la moneda de tu país
            </p>

            <div className="space-y-3 mb-8 text-left max-w-xs mx-auto" data-id="element-111">
              <div className="flex items-center" data-id="element-112">
                <Check className="h-5 w-5 text-green-600 mr-3" data-id="element-113" />
                <span className="font-medium" data-id="element-114">Acceso inmediato</span>
              </div>
              <div className="flex items-center" data-id="element-115">
                <Check className="h-5 w-5 text-green-600 mr-3" data-id="element-116" />
                <span className="font-medium" data-id="element-117">Descarga ilimitada</span>
              </div>
              <div className="flex items-center" data-id="element-118">
                <Check className="h-5 w-5 text-green-600 mr-3" data-id="element-119" />
                <span className="font-medium" data-id="element-120">Garantía de 7 días</span>
              </div>
            </div>

            <CTAButton fullWidth size="lg" className="mb-4" data-id="element-121">
              SI, QUIERO EL MEGAPACK
            </CTAButton>

            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 mt-4" data-id="element-122">
              <Shield className="h-4 w-4" data-id="element-123" />
              <span data-id="element-124">Pago 100% seguro y encriptado</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white px-4" data-id="element-125">
        <div className="max-w-6xl mx-auto" data-id="element-126">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16" data-id="element-127">
            Lo que dicen otros docentes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-128">
            <TestimonialCard name="Laura Fernández" role="Profesora de Química" location="Buenos Aires, Argentina" content="Me devolvió el control de mi año docente. Ya no paso los domingos planificando." initials="LF" data-id="element-129" />
            <TestimonialCard name="Carlos Méndez" role="Docente de Secundaria" location="Santiago, Chile" content="Nunca tuve un material tan completo. Mis alumnos entienden mucho mejor y participan más." initials="CM" data-id="element-130" />
            <TestimonialCard name="Ana García" role="Maestra de Ciencias" location="Madrid, España" content="Mis clases cambiaron por completo. Ahora enseño con orden y tranquilidad." initials="AG" data-id="element-131" />
            <TestimonialCard name="Roberto Silva" role="Profesor de Química" location="Lima, Perú" content="La calidad de los materiales es excelente. Las guías de laboratorio son mis favoritas." initials="RS" data-id="element-132" />
            <TestimonialCard name="María Torres" role="Docente de Ciencias" location="Ciudad de México, México" content="Es la mejor inversión que hice este año. Todo está listo para imprimir y usar." initials="MT" data-id="element-133" />
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-134">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 flex flex-col md:flex-row items-center gap-8" data-id="element-135">
          <div className="flex-shrink-0" data-id="element-136">
            <div className="w-32 h-32 bg-yellow-100 rounded-full flex items-center justify-center border-4 border-yellow-300" data-id="element-137">
              <Shield className="h-16 w-16 text-yellow-600" data-id="element-138" />
            </div>
          </div>
          <div className="text-center md:text-left" data-id="element-139">
            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-140">
              Garantía sin vueltas
            </h3>
            <p className="text-lg text-gray-600 mb-6" data-id="element-141">
              Tenés 7 días para probarlo. Si no te sirve → te devolvemos el 100%
              del dinero. Asumimos todo el riesgo. Tu satisfacción es nuestra
              prioridad.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white px-4" data-id="element-142">
        <div className="max-w-3xl mx-auto" data-id="element-143">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-144">
            PREGUNTAS FRECUENTES
          </h2>

          <div className="space-y-2" data-id="element-145">
            <FAQItem question="¿El material es digital o físico?" answer="Todo el contenido es 100% digital. Descarga inmediata. Podés usarlo desde cualquier dispositivo o imprimir lo que necesites." data-id="element-146" />
            <FAQItem question="¿Cómo recibo el MegaPack?" answer="Apenas se confirma tu compra, recibís el acceso inmediato por email." data-id="element-147" />
            <FAQItem question="¿Sirve para todos los niveles?" answer="Sí. Incluye material organizado separado por nivel." data-id="element-148" />
            <FAQItem question="¿Necesito experiencia previa?" answer="No. Todo está explicado paso a paso." data-id="element-149" />
            <FAQItem question="¿Puedo usarlo en clases presenciales y virtuales?" answer="Sí. Totalmente adaptable a cualquier formato." data-id="element-150" />
            <FAQItem question="¿Cuánto tiempo tengo acceso?" answer="Acceso de por vida." data-id="element-151" />
            <FAQItem question="¿Es seguro comprar?" answer="Sí. Pagos protegidos y encriptados." data-id="element-152" />
            <FAQItem question="¿Qué pasa si no me sirve?" answer="Tenés 7 días de garantía. Reembolso total sin vueltas." data-id="element-153" />
          </div>

          <div className="mt-12 text-center" data-id="element-154">
            <CTAButton scrollToOffer={true} data-id="element-155">QUIERO EMPEZAR AHORA</CTAButton>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center" data-id="element-156">
        <div className="max-w-6xl mx-auto" data-id="element-157">
          <p className="mb-4 font-bold text-white tracking-wider" data-id="element-158">
            MEGAPACK QUÍMICA LISTA™
          </p>
          <p className="text-sm" data-id="element-159">Todos los derechos reservados © 2026</p>
          <div className="mt-8 flex justify-center space-x-4 text-xs" data-id="element-160">
            <span data-id="element-161">Privacidad</span>
            <span data-id="element-162">•</span>
            <span data-id="element-163">Términos</span>
            <span data-id="element-164">•</span>
            <span data-id="element-165">Contacto</span>
          </div>
        </div>
      </footer>
    </div>;
}